import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Trash2, MessagesSquare, PiggyBank, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const TX_LABEL = {
  purchase: "Purchase", cashback: "Cashback", referral: "Referral",
  bonus: "Bonus", gift: "Gift", withdrawal: "Withdrawal", ajo: "Ajo",
};

export default function HistoryPage() {
  const [sessions, setSessions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [s, p, t] = await Promise.all([
        api.get("/chat/sessions"),
        api.get("/ajo/plans"),
        api.get("/transactions"),
      ]);
      setSessions(s.data); setPlans(p.data); setTxs(t.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const deleteSession = async (id) => { await api.delete(`/chat/sessions/${id}`); toast.success("Chat deleted"); load(); };
  const deletePlan = async (id) => { await api.delete(`/ajo/plans/${id}`); toast.success("Plan deleted"); load(); };

  const filtered = filter === "all" ? txs : txs.filter((t) => t.type === filter);
  const isEarning = (type) => ["cashback", "referral", "bonus", "gift"].includes(type);

  return (
    <div className="mx-auto max-w-6xl px-6 py-10 md:px-10">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">History</p>
      <h1 className="mt-2 font-display text-3xl font-medium tracking-tight sm:text-4xl">Transaction history</h1>

      {/* Transactions */}
      <section className="mt-8 rounded-2xl border border-border bg-card" data-testid="history-transactions">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border p-4">
          <div className="flex items-center gap-2"><Receipt className="h-4 w-4 text-primary" /><h2 className="font-display text-lg">All transactions</h2></div>
          <div className="flex flex-wrap gap-1">
            {["all", "purchase", "cashback", "referral", "bonus", "gift", "withdrawal", "ajo"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`tx-filter-${f}`}
                className={`rounded-full px-3 py-1 text-xs capitalize transition-colors ${filter === f ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:bg-secondary/80"}`}
              >{f}</button>
            ))}
          </div>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-muted-foreground">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground">No transactions yet. Start using Zuntria to earn rewards!</div>
        ) : (
          <div className="divide-y divide-border">
            {filtered.map((t) => (
              <div key={t.tx_id} className="flex items-center justify-between px-4 py-3 text-sm" data-testid={`tx-row-${t.tx_id}`}>
                <div>
                  <div className="font-medium">{TX_LABEL[t.type] || t.type}</div>
                  <div className="mt-0.5 text-xs text-muted-foreground">{t.description}</div>
                </div>
                <div className="text-right">
                  <div className={`font-medium ${isEarning(t.type) ? "text-[hsl(var(--success))]" : (t.type === "purchase" || t.type === "withdrawal") ? "text-destructive" : ""}`}>
                    {isEarning(t.type) ? "+" : (t.type === "purchase" || t.type === "withdrawal") ? "-" : ""}
                    {t.value_kind === "points" ? `${t.amount} pts` : (t.type === "bonus" || t.type === "gift") && !t.value_kind ? t.amount : `£${Number(t.amount).toFixed(2)}`}
                  </div>
                  <div className="text-xs text-muted-foreground">{new Date(t.date).toLocaleDateString()}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-2">
        <section data-testid="history-chats">
          <div className="mb-3 flex items-center gap-2"><MessagesSquare className="h-4 w-4 text-primary" /><h2 className="font-display text-xl">Chats</h2></div>
          {sessions.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-6 text-sm text-muted-foreground">No saved chats yet.</div>
          ) : (
            <div className="divide-y divide-border rounded-2xl border border-border bg-card">
              {sessions.map((s) => (
                <div key={s.session_id} className="flex items-center justify-between gap-4 p-4">
                  <Link to={`/chat/${s.assistant_id}?session=${s.session_id}`} className="min-w-0 flex-1" data-testid={`session-link-${s.session_id}`}>
                    <div className="truncate text-sm font-medium">{s.title || "Untitled chat"}</div>
                    <div className="mt-1 text-xs text-muted-foreground capitalize">{s.assistant_id} · {new Date(s.updated_at).toLocaleString()}</div>
                  </Link>
                  <Button variant="ghost" size="icon" onClick={() => deleteSession(s.session_id)} data-testid={`delete-session-${s.session_id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>

        <section data-testid="history-plans">
          <div className="mb-3 flex items-center gap-2"><PiggyBank className="h-4 w-4 text-primary" /><h2 className="font-display text-xl">Ajo plans</h2></div>
          {plans.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-6 text-sm text-muted-foreground">No saved Ajo plans yet.</div>
          ) : (
            <div className="divide-y divide-border rounded-2xl border border-border bg-card">
              {plans.map((p) => (
                <div key={p.plan_id} className="flex items-center justify-between gap-4 p-4">
                  <Link to="/ajo" className="min-w-0 flex-1" data-testid={`plan-link-${p.plan_id}`}>
                    <div className="text-sm font-medium">{p.people} members · £{p.amount}</div>
                    <div className="mt-1 text-xs text-muted-foreground">{p.frequency} · starts {p.start_date}</div>
                  </Link>
                  <Button variant="ghost" size="icon" onClick={() => deletePlan(p.plan_id)} data-testid={`delete-plan-${p.plan_id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
